import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { resolveAssetUrl } from '@/shop/assetUrl';
import type { CharacterSkin } from '@shared/protocol';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useMatchStore } from '@/stores/matchStore';
import { useRoomStore } from '@/stores/roomStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { Climber, Trophy } from '@/art';
import { RankUpModal } from './RankUpModal';
import { useT } from '@/i18n/useI18n';
import { audio } from '@/audio/AudioService';
import { REWARD_LOSE_COINS, REWARD_LOSE_XP, REWARD_WIN_COINS, REWARD_WIN_XP } from '@shared/gameConfig';
import { adsAvailable, showRewarded, maybeInterstitialOnReturn } from '@/ads/adService';
import { challengeApi } from '@/challenges/challengeApi';
import { gameCenterChallengeShareUrl } from '@/challenges/gameCenterChallenge';
import { shareGame } from '@/rewards/questApi';

export function ResultScreen() {
  const go = useUIStore((s) => s.go);
  const result = useUIStore((s) => s.matchResult);
  const t = useT();
  const profile = useUserStore((s) => s.profile);
  const setProfile = useUserStore((s) => s.setProfile);
  const maxCombo = useMatchStore((s) => s.maxCombo);
  const timer = useMatchStore((s) => s.timer);
  // Equipped Character skins captured at MATCH_FOUND — used to celebrate the
  // winner with their victory-pose frames (yours on a win, the rival's on a loss).
  const yourCharacter = useMatchStore((s) => s.yourCharacter);
  const rivalCharacter = useMatchStore((s) => s.rivalCharacter);
  const isTeam = useMatchStore((s) => s.isTeam);
  const yourTeam = useMatchStore((s) => s.yourTeam);
  const rivalTeam = useMatchStore((s) => s.rivalTeam);
  const serverSummary = useMatchStore((s) => s.serverSummary);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const room = useRoomStore((s) => s.room);
  const clearRoom = useRoomStore((s) => s.clear);
  const matchSource = useMatchStore((s) => s.matchSource);
  const warId = useMatchStore((s) => s.warId);
  // We came from a private lobby ONLY if this specific match was launched
  // from the room (RoomNotifier sets source='room' on room:matchStart).
  // Without this check, a stale roomStore from earlier in the session would
  // wrongly trigger "BACK TO ROOM" on every quick-match result.
  const fromRoom = matchSource === 'room' && room != null;
  // War attacks must go back to the WarBoard (which re-issues WAR_QUEUE_JOIN with
  // the warId + enforces the attack cap) — never the generic ranked rematch.
  const fromWar = warId != null;
  const fromChallenge = matchSource === 'challenge';
  const challengeRun = useMatchStore((s) => s.challengeRun);
  const challengeResult = useMatchStore((s) => s.challengeResult);
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [challengeMessage, setChallengeMessage] = useState<string | null>(null);

  async function createChallengeFromResult() {
    if (!matchId || challengeBusy) return;
    setChallengeBusy(true);
    setChallengeMessage(null);
    try {
      const { challenge } = await challengeApi.create({ matchId });
      const url = await gameCenterChallengeShareUrl(challenge.code, challenge.targetDurationMs);
      const shared = await shareGame(
        t('challenge.share_text', { name: profile.name, time: `${(timer || 0).toFixed(2)}s` }),
        url,
      );
      setChallengeMessage(shared ? t('challenge.shared') : t('challenge.created'));
    } catch {
      setChallengeMessage(t('challenge.error_default'));
    } finally {
      setChallengeBusy(false);
    }
  }

  async function createChallengeRematch() {
    if (!challengeRun || challengeBusy) return;
    setChallengeBusy(true);
    try {
      const { challenge } = await challengeApi.rematch(challengeRun.code);
      const url = await gameCenterChallengeShareUrl(challenge.code, challenge.targetDurationMs);
      await shareGame(
        t('challenge.rematch_share_text', { time: challengeResult ? `${(challengeResult.durationMs / 1000).toFixed(2)}s` : '' }),
        url,
      );
      setChallengeMessage(t('challenge.rematch_sent'));
    } catch {
      setChallengeMessage(t('challenge.complete_to_rematch'));
    } finally {
      setChallengeBusy(false);
    }
  }

  async function backToClanWar() {
    // Drop match state so the next Quick Match is a clean 1v1, then return to
    // the clan screen where the WarBoard offers another attack if any remain.
    useMatchStore.getState().reset();
    go('clan');
  }

  // Coin pickup chime on mount — short, signals "you earned reward".
  useEffect(() => {
    audio.play('ui.coin_pickup');
    // A match just ended — burn one off any active cosmetic trial (server
    // reverts the equip when it expires). Best-effort.
    void import('@/shop/trialApi').then(({ trialApi }) => trialApi.consume().catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function leaveRoomAndHome() {
    if (room) {
      const { roomApi } = await import('@/rooms/roomApi');
      try { await roomApi.leave(room.code); } catch { /* noop */ }
    }
    clearRoom();
    // Drop any pending-2v2 / team state so the next Quick Match is a clean 1v1.
    useMatchStore.getState().reset();
    await maybeInterstitialOnReturn();
    go('home');
  }

  // Show the Wild-Rift-style rank overlay first if the server reported a
  // promotion/demotion. The actual result content sits behind it; tapping the
  // modal (or 4s timeout) dismisses it.
  const progression = serverSummary?.rankProgression ?? null;
  const [showRankModal, setShowRankModal] = useState<boolean>(
    Boolean(progression && (progression.promoted || progression.demoted)),
  );

  // Refresh authoritative profile from the server so MMR / coins / XP shown on
  // this screen and the home screen match what the database committed.
  useEffect(() => {
    if (!token) return;
    authApi
      .me(token)
      .then(({ user }) => {
        setUser(user);
        setProfile({
          id: user.id,
          name: user.displayName,
          avatarColor: user.avatarColor,
          avatarUrl: user.avatarUrl,
          mmr: user.mmr,
          rank: user.rank,
          rankDivision: user.rankDivision,
          rankStars: user.rankStars,
          coins: user.coins,
          gems: user.gems,
          xp: user.xp,
          winStreak: user.winStreak,
          matchesPlayed: user.matchesPlayed,
          matchesWon: user.matchesWon,
          frame: user.frame ?? null,
        });
      })
      .catch((e) => console.warn('[result] profile refresh failed', e));
  }, [token, setUser, setProfile]);

  const win = result === 'win';
  // A 2v2 result celebrates BOTH members of the winning team. Each client
  // receives team lists from its own perspective, so choose the local team on
  // victory and the rival team on defeat.
  const winningTeam = win ? yourTeam : rivalTeam;
  const showWinningTeam = isTeam && winningTeam.length > 0;
  const winnerShouts = serverSummary?.winnerShouts ?? [];
  // Prefer server-issued numbers (truth); fall back to the local heuristic so
  // offline AI matches still look reasonable.
  const mmrDelta = serverSummary?.mmrDelta.you ?? (win ? 24 : -18);
  const baseCoin = serverSummary?.rewards.coins ?? (win ? REWARD_WIN_COINS : REWARD_LOSE_COINS);
  const baseXp = serverSummary?.rewards.xp ?? (win ? REWARD_WIN_XP : REWARD_LOSE_XP);

  // Rewarded-ad "x2 rewards": grants an extra 1x server-side (online matches
  // only). Shown as a doubled value once claimed.
  const matchId = useMatchStore((s) => s.matchId);
  const mode = useMatchStore((s) => s.mode);
  const [x2Claimed, setX2Claimed] = useState(false);
  const [x2Busy, setX2Busy] = useState(false);
  const coinReward = x2Claimed ? baseCoin * 2 : baseCoin;
  const xpReward = x2Claimed ? baseXp * 2 : baseXp;
  const canX2 = adsAvailable() && mode === 'online' && !!matchId && !x2Claimed;

  async function claimX2() {
    if (!matchId || x2Busy) return;
    setX2Busy(true);
    try {
      const earned = await showRewarded();
      if (!earned) { setX2Busy(false); return; }
      const { matchApi } = await import('@/match/matchApi');
      const r = await matchApi.x2Bonus(matchId);
      setProfile({ coins: r.coins, gems: r.gems, xp: r.xp });
      setX2Claimed(true);
      audio.play('ui.coin_pickup');
    } catch {
      audio.play('ui.error');
    }
    setX2Busy(false);
  }
  // Real battle stats from the authoritative server tap log (1v1). Absent for
  // offline/local or team matches → fall back to local-only values (you side)
  // and dash out what we can't know (rival).
  const stats = serverSummary?.stats ?? null;
  const fmtTime = (ms: number) => `${(ms / 1000).toFixed(1)}s`;
  const localDurationS = serverSummary ? serverSummary.durationMs / 1000 : timer;

  const youSpeed = stats
    ? fmtTime(stats.you.timeMs)
    : localDurationS > 0
      ? `${localDurationS.toFixed(1)}s`
      : '—';
  const rivalSpeed = stats ? fmtTime(stats.rival.timeMs) : '—';
  const youAccuracy = stats ? `${stats.you.accuracy}%` : '—';
  const rivalAccuracy = stats ? `${stats.rival.accuracy}%` : '—';
  const youCombo = stats ? `×${stats.you.maxCombo}` : maxCombo > 0 ? `×${maxCombo}` : '—';
  const rivalCombo = stats ? `×${stats.rival.maxCombo}` : '—';

  // Per-row highlight: pick the genuinely better number when we have real data;
  // otherwise fall back to the match winner.
  const speedWinner = stats
    ? stats.you.timeMs <= stats.rival.timeMs
      ? 'you'
      : 'rival'
    : win
      ? 'you'
      : 'rival';
  const accuracyWinner = stats
    ? stats.you.accuracy >= stats.rival.accuracy
      ? 'you'
      : 'rival'
    : win
      ? 'you'
      : 'rival';
  const comboWinner = stats
    ? stats.you.maxCombo >= stats.rival.maxCombo
      ? 'you'
      : 'rival'
    : win
      ? 'you'
      : 'rival';

  return (
    <div
      className="screen screen-fit screen-fit--dense scanlines"
      style={{
        background: win
          ? 'radial-gradient(ellipse at center top, rgba(244,199,36,0.25), var(--bg-deep) 60%)'
          : 'radial-gradient(ellipse at center top, rgba(230,57,70,0.2), var(--bg-deep) 60%)',
      }}
    >
      <div className="hex-bg" />

      <div style={{ paddingTop: 'clamp(8px,2vh,18px)', textAlign: 'center', flexShrink: 0 }}>
        <div className="tag" style={{ color: win ? 'var(--c-yellow)' : 'var(--c-red)' }}>
          {win ? `★ ${t('result.match_result')}` : `✕ ${t('result.match_result')}`}
        </div>
        <div
          className="display shadow-text"
          style={{
            fontSize: 'clamp(34px,8vh,56px)',
            marginTop: 6,
            color: win ? 'var(--c-yellow)' : 'var(--ink)',
            letterSpacing: '0.04em',
          }}
        >
          {win ? t('result.victory') : t('result.defeat')}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-muted)',
            marginTop: 4,
            fontFamily: 'var(--f-mono)',
          }}
        >
          {fromChallenge
            ? t('challenge.no_mmr')
            : win
              ? `${profile.rank.toUpperCase()} · +★`
              : `${profile.rank.toUpperCase()} · −★`}
        </div>
      </div>

      {/* Loser sees the winner's Victory Shout(s) roaming across the screen —
          same taunting overlay for 1v1 and 2v2 (1v1 has a single shout). */}
      {!win && winnerShouts.length > 0 && (
        <RoamingShoutOverlay shouts={winnerShouts} />
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 'clamp(6px,1.5vh,18px)',
          flex: '1 1 0',
          minHeight: 56,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {showWinningTeam ? (
          <div
            style={{
              display: 'flex',
              gap: 'clamp(10px,4vw,32px)',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            {winningTeam.map((m, idx) => (
              <VictoryCharacter
                key={m.id || idx}
                skin={m.character ?? null}
                glow={win ? 'rgba(244,199,36,0.65)' : 'rgba(230,57,70,0.5)'}
                fallback={
                  <div style={{ transform: 'scale(1.7)' }}>
                    <Climber color={idx === 0 ? 'blue' : 'red'} pose="victory" />
                  </div>
                }
              />
            ))}
          </div>
        ) : win ? (
          <VictoryCharacter
            skin={yourCharacter}
            glow="rgba(244,199,36,0.65)"
            fallback={<Trophy size={140} />}
          />
        ) : (
          <VictoryCharacter
            skin={rivalCharacter}
            glow="rgba(230,57,70,0.5)"
            fallback={
              <div style={{ transform: 'scale(2.2)' }}>
                <Climber color="blue" pose="victory" />
              </div>
            }
          />
        )}
      </div>

      <div style={{ padding: 'clamp(10px,2vh,24px) 18px 0', flexShrink: 0 }}>
        <div className="card" style={{ padding: 'clamp(10px,1.6vh,14px)' }}>
          <div className="tag" style={{ marginBottom: 'clamp(6px,1.2vh,12px)', textAlign: 'center' }}>
            {t('result.rewards')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <RewardBox
              label="MMR"
              value={`${mmrDelta > 0 ? '+' : ''}${mmrDelta}`}
              color={mmrDelta > 0 ? 'var(--c-cyan)' : 'var(--c-red)'}
            />
            <RewardBox label={t('result.coins')} value={`+${coinReward}`} color="var(--c-yellow)" />
            <RewardBox label="XP" value={`+${xpReward}`} color="var(--c-purple)" />
          </div>

          {canX2 && (
            <button
              onClick={claimX2}
              disabled={x2Busy}
              style={{
                marginTop: 10,
                width: '100%',
                padding: '11px 14px',
                borderRadius: 12,
                border: '1px solid var(--c-yellow)',
                background: 'linear-gradient(135deg, rgba(244,199,36,0.18), rgba(244,199,36,0.32))',
                color: 'var(--c-yellow)',
                fontFamily: 'var(--f-display)',
                fontSize: 14,
                letterSpacing: '0.04em',
                cursor: x2Busy ? 'default' : 'pointer',
                boxShadow: '0 0 18px rgba(244,199,36,0.35)',
              }}
            >
              {x2Busy ? t('result.x2_watching') : `📺 ${t('result.x2_button')}`}
            </button>
          )}
          {x2Claimed && (
            <div
              style={{
                marginTop: 8,
                textAlign: 'center',
                fontSize: 11,
                color: 'var(--c-yellow)',
                fontFamily: 'var(--f-display)',
              }}
            >
              ✓ {t('result.x2_done')}
            </div>
          )}
        </div>
      </div>

      <div style={{ padding: 'clamp(8px,1.4vh,12px) 18px 0', flexShrink: 0 }}>
        <div className="card" style={{ padding: 'clamp(10px,1.6vh,14px)' }}>
          <div className="tag" style={{ marginBottom: 'clamp(6px,1.2vh,10px)' }}>
            {t('result.battle_stats')}
          </div>
          <StatRow
            label={t('result.stat_speed')}
            you={youSpeed}
            rival={rivalSpeed}
            winner={speedWinner}
          />
          <StatRow
            label={t('result.stat_accuracy')}
            you={youAccuracy}
            rival={rivalAccuracy}
            winner={accuracyWinner}
          />
          <StatRow
            label={t('result.stat_combo_max')}
            you={youCombo}
            rival={rivalCombo}
            winner={comboWinner}
          />
        </div>
      </div>

      <div style={{ padding: 'clamp(8px,1.6vh,14px) 18px max(12px,env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        {fromChallenge ? (
          <>
            {challengeResult?.steps === challengeRun?.steps && (
              <button className="btn btn-primary" disabled={challengeBusy} onClick={() => void createChallengeRematch()}>
                ⚔ {challengeBusy ? t('common.loading') : t('challenge.challenge_back')}
              </button>
            )}
            {challengeMessage && <div style={{ color: 'var(--c-cyan)', textAlign: 'center', fontSize: 11 }}>{challengeMessage}</div>}
            <button className="btn btn-ghost" onClick={() => { useMatchStore.getState().reset(); go('friends'); }}>
              {t('challenge.back_to_challenges')}
            </button>
          </>
        ) : fromWar ? (
          <>
            <button className="btn btn-primary" onClick={backToClanWar}>
              {t('result.back_to_war')}
            </button>
            <button
              className="btn btn-ghost"
              onClick={async () => { useMatchStore.getState().reset(); await maybeInterstitialOnReturn(); go('home'); }}
            >
              {t('result.home')}
            </button>
          </>
        ) : fromRoom ? (
          <>
            <button className="btn btn-primary" onClick={() => go('room')}>
              {t('result.back_to_room')}
            </button>
            <button className="btn btn-ghost" onClick={leaveRoomAndHome}>
              {t('result.leave_room')}
            </button>
          </>
        ) : (
          <>
            {win && mode === 'online' && matchId && (
              <button className="btn btn-primary" disabled={challengeBusy} onClick={() => void createChallengeFromResult()}>
                ⚔ {challengeBusy ? t('common.loading') : t('challenge.share_result')}
              </button>
            )}
            {challengeMessage && <div style={{ color: 'var(--c-cyan)', textAlign: 'center', fontSize: 11 }}>{challengeMessage}</div>}
            <button className="btn btn-primary" onClick={() => go('matching')}>
              {t('result.rematch')}
            </button>
            <button
              className="btn btn-ghost"
              onClick={async () => { await maybeInterstitialOnReturn(); go('home'); }}
            >
              {t('result.home')}
            </button>
          </>
        )}
      </div>

      {showRankModal && progression && (
        <RankUpModal
          progression={progression}
          onDismiss={() => setShowRankModal(false)}
        />
      )}
    </div>
  );
}

/** Celebrates the winner with their Character's victory-pose art. Plays the
 * win frames ([base, ...extra]) as a ~6fps flipbook; when the character has no
 * win art (or no equipped character) it renders `fallback` instead (trophy on a
 * win, a generic cheering climber on a loss). */
function VictoryCharacter({
  skin,
  glow,
  fallback,
}: {
  skin: CharacterSkin | null;
  glow: string;
  fallback: ReactNode;
}) {
  const frames = useMemo(() => {
    if (!skin) return [] as string[];
    // Prefer the win-pose flipbook, but fall back through the character's other
    // poses so ANY equipped character still shows up — the loser must see the
    // winner's character even when only an idle/climb PNG was uploaded.
    const list = skin.winFrames?.length
      ? skin.winFrames
      : skin.winUrl
        ? [skin.winUrl]
        : skin.idleUrl
          ? [skin.idleUrl]
          : skin.climbUrl
            ? [skin.climbUrl]
            : [];
    return list.map(resolveAssetUrl).filter((u): u is string => !!u);
  }, [skin]);

  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (frames.length < 2) return;
    const id = window.setInterval(() => setI((p) => (p + 1) % frames.length), 1000 / 6);
    return () => window.clearInterval(id);
  }, [frames]);

  if (!frames.length) return <>{fallback}</>;
  // Mount every frame as its own persistent <img> and animate by toggling
  // opacity. Swapping a single <img>'s `src` re-hits the network on each cycle
  // in some environments (ngrok / disabled disk cache); keeping each element
  // mounted means each URL is fetched exactly once and never re-requested.
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        height: '100%',
        maxHeight: 150,
        animation: 'victory-cheer 1.1s ease-in-out infinite',
        filter: `drop-shadow(0 0 16px ${glow}) drop-shadow(0 8px 8px rgba(0,0,0,0.5))`,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {frames.map((src, idx) => (
        <img
          key={src}
          src={src}
          alt=""
          draggable={false}
          style={{
            height: '100%',
            maxHeight: 150,
            width: 'auto',
            objectFit: 'contain',
            display: 'block',
            // The active frame sits in normal flow (defines the box); the rest
            // overlay it invisibly so all stay loaded.
            position: idx === i ? 'relative' : 'absolute',
            top: 0,
            left: 0,
            opacity: idx === i ? 1 : 0,
          }}
        />
      ))}
    </div>
  );
}

/** Loser defeat overlay: cycles through the winner's victory shout(s),
 *  popping one taunt bubble per second at a random spot on the screen. */
function RoamingShoutOverlay({ shouts }: { shouts: Array<{ text: string }> }) {
  const t = useT();
  const [bubble, setBubble] = useState<{ text: string; key: number; top: number; left: number } | null>(
    null,
  );
  useEffect(() => {
    if (!shouts.length) return;
    let idx = 0;
    let key = 0;
    const pop = () => {
      const s = shouts[idx % shouts.length];
      idx += 1;
      key += 1;
      setBubble({
        text: s.text,
        key,
        top: 16 + Math.random() * 62, // 16%–78% down
        left: 18 + Math.random() * 64, // 18%–82% across
      });
    };
    pop();
    const id = window.setInterval(pop, 1000);
    return () => window.clearInterval(id);
  }, [shouts]);

  if (!bubble) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 45, overflow: 'hidden' }}>
      {/* Outer: positions + handles the pop/scale/fade lifecycle. */}
      <div
        key={bubble.key}
        style={{
          position: 'absolute',
          top: `${bubble.top}%`,
          left: `${bubble.left}%`,
          maxWidth: '80vw',
          animation: 'roamShout 1s ease-out forwards',
        }}
      >
        {/* Inner: aggressive in-your-face wiggle (separate transform context so
            it never fights the outer pop animation). */}
        <div
          style={{
            padding: '10px 20px',
            borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(230,57,70,0.34), rgba(150,28,38,0.46))',
            border: '2px solid rgba(255,92,106,0.95)',
            boxShadow:
              '0 0 26px rgba(230,57,70,0.9), 0 0 52px rgba(230,57,70,0.5), inset 0 0 12px rgba(255,120,130,0.35)',
            textAlign: 'center',
            animation: 'shoutTaunt 0.3s ease-in-out infinite',
          }}
        >
          <div
            style={{
              fontSize: 9,
              letterSpacing: '0.18em',
              color: '#FFD7DB',
              fontFamily: 'var(--f-display)',
              marginBottom: 3,
              textShadow: '0 0 8px rgba(230,57,70,0.9)',
            }}
          >
            😈 {t('result.opponent_shouts')}
          </div>
          <div
            className="display"
            style={{
              fontSize: 'clamp(20px,6vw,30px)',
              color: '#fff',
              letterSpacing: '0.01em',
              textShadow:
                '0 0 12px rgba(255,70,84,1), 0 0 24px rgba(230,57,70,0.85), 0 2px 2px rgba(0,0,0,0.7)',
              wordBreak: 'break-word',
              lineHeight: 1.1,
            }}
          >
            “{bubble.text}”
          </div>
        </div>
      </div>
    </div>
  );
}

interface RewardBoxProps {
  label: string;
  value: string;
  color: string;
}

function RewardBox({ label, value, color }: RewardBoxProps) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 12,
        background: 'rgba(0,0,0,0.35)',
        border: '1px solid var(--hairline)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 9,
          color: 'var(--ink-dim)',
          letterSpacing: '0.1em',
        }}
      >
        {label}
      </div>
      <div
        className="display"
        style={{ fontSize: 22, color, marginTop: 4, textShadow: `0 0 10px ${color}` }}
      >
        {value}
      </div>
    </div>
  );
}

interface StatRowProps {
  label: string;
  you: string;
  rival: string;
  winner: 'you' | 'rival';
}

function StatRow({ label, you, rival, winner }: StatRowProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 60px 1fr',
        alignItems: 'center',
        padding: '7px 0',
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <div style={{ textAlign: 'right' }}>
        <span
          className="mono"
          style={{
            fontSize: 13,
            color: winner === 'you' ? 'var(--c-yellow)' : 'var(--ink-muted)',
            textShadow: winner === 'you' ? '0 0 8px rgba(244,199,36,0.5)' : 'none',
          }}
        >
          {you}
        </span>
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: 10,
          color: 'var(--ink-dim)',
          fontFamily: 'var(--f-display)',
          letterSpacing: '0.08em',
        }}
      >
        {label}
      </div>
      <div>
        <span
          className="mono"
          style={{
            fontSize: 13,
            color: winner === 'rival' ? 'var(--c-yellow)' : 'var(--ink-muted)',
            textShadow: winner === 'rival' ? '0 0 8px rgba(244,199,36,0.5)' : 'none',
          }}
        >
          {rival}
        </span>
      </div>
    </div>
  );
}
